import React, { useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { api } from '../../api/api';

export const ProfileModal = ({ onClose }) => {
  const { user, resetOnboarding, activeGroupId, setActiveGroupId } = useAuth();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [replayState, setReplayState] = useState('idle');
  const [replayMessage, setReplayMessage] = useState('');
  const handleReplayOnboarding = async () => {
    if (!user || replayState === 'pending') return;
    setReplayState('pending');
    setReplayMessage('');
    try {
      const result = await resetOnboarding();
      if (result.success) {
        setReplayState('success');
        setReplayMessage('Onboarding will restart when you return to the app.');
        onClose();
      } else {
        setReplayState('error');
        setReplayMessage(result.message || 'Unable to reset onboarding.');
      }
    } catch (err) {
      setReplayState('error');
      setReplayMessage(err.message || 'Unable to reset onboarding.');
    }
  };
  const [loading, setLoading] = useState(false);

  const assignedGroups = useMemo(() => {
    if (!user) return [];
    const map = new Map();
    const primaryId = Number(user?.group?.id || user?.groupId || user?.group_id);
    if (Number.isFinite(primaryId) && primaryId > 0) {
      map.set(primaryId, user?.group?.name || `Group ${primaryId}`);
    }
    if (Array.isArray(user?.groups)) {
      user.groups.forEach(group => {
        const gid = Number(group?.id || group?.groupId || group?.group_id);
        if (Number.isFinite(gid) && gid > 0) {
          map.set(gid, group?.name || `Group ${gid}`);
        }
      });
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [user]);

  const activeGroupValue = useMemo(() => {
    const parsed = Number(activeGroupId);
    return Number.isFinite(parsed) ? parsed : '';
  }, [activeGroupId]);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }

    setLoading(true);
    const result = await api.changePassword(user.id, newPassword, oldPassword);
    setLoading(false);

    if (result.success) {
      setSuccess('Password changed successfully!');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setError(result.message || 'An error occurred.');
    }
  };

  return (
    <div
      className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 px-4 py-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white p-6 sm:p-8 rounded-2xl shadow-2xl w-full max-w-md transform transition-all animate-fadeIn max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">My Profile</h2>
          <button
            onClick={onClose}
            className="text-2xl text-gray-500 hover:text-gray-800"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <p className="p-2 bg-gray-100 rounded-md text-gray-700">
              {user.username}
            </p>
          </div>
          {assignedGroups.length > 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Active group
              </label>
              <select
                value={activeGroupValue}
                onChange={e => setActiveGroupId(Number(e.target.value) || null)}
                className="form-select w-full"
              >
                {assignedGroups.map(group => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Switching groups updates permissions across the app.
              </p>
            </div>
          )}
          <input
            type="password"
            value={oldPassword}
            onChange={e => setOldPassword(e.target.value)}
            placeholder="Current Password"
            className="form-input"
            required
          />
          <input
            type="password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            placeholder="New Password"
            className="form-input"
            required
          />
          <input
            type="password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            placeholder="Confirm New Password"
            className="form-input"
            required
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white py-2.5 rounded-md font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400"
          >
            {loading ? 'Saving...' : 'Change Password'}
          </button>
          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
          {success && <p className="text-green-600 text-sm mt-2">{success}</p>}
        </form>
        <div className="mt-6 border-t border-slate-100 pt-5 text-center space-y-2">
          <p className="text-xs font-semibold text-slate-500">Replay onboarding</p>
          <button
            type="button"
            onClick={handleReplayOnboarding}
            disabled={replayState === 'pending'}
            className="w-full rounded-full border border-slate-300 bg-white py-2 text-xs font-semibold text-slate-700 transition hover:border-primary hover:text-primary disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {replayState === 'pending' ? 'Resetting…' : 'Replay onboarding tour'}
          </button>
          {replayMessage && (
            <p className={`text-xs ${replayState === 'error' ? 'text-rose-500' : 'text-green-600'}`}>
              {replayMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
